/* cf-perfmesh-cut-parts.js
 * ---------------------------------------------------------------------------
 * Bill the extra framing a cut PerfMesh panel needs.
 *
 * Pete, 2026-08-25: "per cut panel perfmesh requires 2x 1105 frame 2x 1105 clip
 * and 2x channel."
 *
 * Why this exists.  A PerfMesh panel kit is framed on all four sides: the 2m/3m
 * frame and clip make the top and bottom rails, two 1105mm frames and clips make
 * the vertical stiles, and two 1185mm channels fix the assembly to the posts.
 * When a run does not divide evenly into 2m and 3m panels, the last panel is cut
 * from a full kit -- and the cut end arrives with no stile and no channel.  So a
 * cut panel costs a full kit PLUS a second set of verticals.  Blade and flat top
 * have the same idea and call it extra brackets; on PerfMesh it is frame, clip
 * and channel.
 *
 * Where it hooks, and why there.
 * ------------------------------
 * calculatePanelCosts(section, calculation) is the ONE place the perfmesh
 * calculator turns a drawn layout into money: it pushes into
 * state.quoteBreakdown and state.cartItems and returns the cost it added.  The
 * on-screen breakdown, the saved quote and the cart are all rendered from those
 * two arrays afterwards.
 *
 * That matters, because the alternative was tried and it cost money.  Earlier in
 * this run a patch rewrote the *rendered* breakdown separately from the array it
 * was rendered from, and the two counted the same panels twice: 12300mm on 3000
 * sheets quoted $3,252.50 against a true $2,012.48.  Appending to the source
 * array instead makes screen and quote agree by construction rather than by
 * two pieces of code being kept in step.  There is nothing to keep in step.
 *
 * What counts as a cut panel.
 * ---------------------------
 * Kits are stocked at 2000mm and 3000mm finished width.  Any other width had to
 * be cut down, so: width !== 2000 && width !== 3000 -- EXCEPT a panel of 970mm or
 * less, which is not a cut-down 2m kit at all but a 1m PerfMesh Panel Kit, and
 * carries its own stiles and channel already.  See the section below.
 *
 * The safety valve.
 * -----------------
 * The conversion kit is a real Shopify product with a real SKU per colour.  If
 * that SKU does not resolve to a variant id -- because the product is still in
 * draft, or has not reached the theme's product data yet -- this adds NOTHING:
 * no line, no cost, no cart item.  A quote that silently omits a part is a
 * merchandising gap Pete can see and fix; a quote carrying a line item that
 * cannot resolve at checkout is an order that fails at the till.  So it fails
 * closed, and says so in the console once.
 *
 * The 1m PerfMesh Panel Kit, and why it lives in this same file.
 * -------------------------------------------------------------
 * Settled in click-fencing-BOM-rules-posts-brackets-panelkits-VERIFIED-2026-08-09:
 * a PerfMesh panel of 970mm or less is ONE product, the 1m PerfMesh Panel Kit, and
 * pricing is flat under a metre -- a 400mm section and a 950mm section are the same
 * kit, cut to suit.  The calculator does not know that: it bills every panel of
 * 2000mm or less as a 2 metre kit.
 *
 * These two rules cannot be written apart, because the 1m kit ALREADY contains 2 x
 * 1105 frame, 2 x 1105 clip and 2 x 1185 channel -- the exact contents of the
 * conversion kit.  Billing both would charge the customer twice for the same six
 * extrusions.  So the small-panel swap and the cut-panel surcharge are decided in one
 * pass over the same list of widths, and a panel can only ever be one or the other.
 *
 * The cut-off is 970, not 1000.  The kit is built from the 1140x930 mesh sheet and
 * finishes at 970mm, so 970 is the widest panel it can actually make.  A 990mm panel
 * is not "under a metre, near enough" -- it has to come off a 2m kit and be cut, and
 * it needs the conversion kit like any other cut panel.
 * --------------------------------------------------------------------------- */

(function () {
  'use strict';
  if (window.__cfPerfmeshCutPartsInstalled) return;
  window.__cfPerfmeshCutPartsInstalled = 1;

  var LOG = '[cf-perfmesh-cut-parts]';
  var SKU = { black: 'PERF-CUT-KIT-BLK', white: 'PERF-CUT-KIT-WHT', monument: 'PERF-CUT-KIT-MON' };
  var SKU_1M = { black: 'PM-PK-1000-SB', white: 'PM-PK-1000-WHT', monument: 'PM-PK-1000-MON' };
  var KIT_2M = /^PERF-KIT-2M-/i;
  var SMALL_MAX = 970;                      /* the widest panel a 1m kit can make */
  var LABEL = { black: 'Satin Black', white: 'White', monument: 'Monument' };
  var FULL = { 2000: 1, 3000: 1 };          /* the widths a kit is stocked in */
  var warned = 0, warned1m = 0;

  function num(x) { var n = parseFloat(x); return isFinite(n) ? n : 0; }
  function round2(n) { return Math.round(n * 100) / 100; }
  function isArr(x) { return Object.prototype.toString.call(x) === '[object Array]'; }

  function colourKey(gc, section) {
    var s = '';
    try {
      s = String((gc.resolveSectionColour ? gc.resolveSectionColour(section)
                                          : (section && (section.colour || section.color))) || '');
    } catch (e) { s = ''; }
    s = s.toLowerCase();
    if (s.indexOf('white') >= 0) return 'white';
    if (s.indexOf('monument') >= 0) return 'monument';
    return 'black';
  }

  /* Price and variant come from the same two globals the calculator itself uses
     for every other line, so a price change in Shopify moves this line too. */
  function resolve(sku) {
    var id = null, cents = null;
    try { if (typeof window.CF_VARIANT === 'function') id = window.CF_VARIANT(sku) || null; } catch (e) {}
    if (!id) {
      try {
        var gc = window.glassCalculator || window.glassFencingCalculator;
        var map = gc && gc.shopifyData && gc.shopifyData.skuVariantMap;
        if (map && map[sku]) id = map[sku];
      } catch (e2) {}
    }
    try { if (typeof window.CF_PRICE === 'function') cents = window.CF_PRICE(sku); } catch (e3) {}
    if (!id || !(num(cents) > 0)) return null;
    return { variantId: id, price: num(cents) / 100, sku: sku };
  }

  /* The widths the calculator just billed for.  Mirrors calculatePanelCosts'
     own three sources in the same order -- cut plan, then rendered panels, then
     the raw calculation -- so this can never disagree with what was charged. */
  function widthsFor(gc, section, calculation) {
    var gateLen = parseInt(section && section.gateLength, 10) || 0;
    try {
      var key = null, k;
      for (k in gc.state.sections) {
        if (gc.state.sections[k] === section) { key = k; break; }
      }
      if (key !== null && typeof gc.calculateJobCutPlan === 'function') {
        var plan = gc.calculateJobCutPlan()[key];
        if (plan && plan.valid && plan.panels && plan.panels.length) {
          /* No width test. The cut plan does not contain the gate at all -- verified
             2026-08-26, every entry has is_gate undefined and the gate's 1000mm is
             absent -- so `p.sheet === gateLen` can only ever delete a real panel that
             happens to measure the same as the gate. That is the bug this file used to
             copy from calculatePanelCosts; see cf-gate-panel-billing.js. */
          return plan.panels.filter(function (p) {
            return !p.is_gate && !p.is_hinge;
          }).map(function (p) { return p.sheet; });
        }
      }
    } catch (e) {}
    if (section && isArr(section.renderedPanels) && section.renderedPanels.length) {
      /* renderedPanels types the gate ({ type: 'gate', isGate: true }), so the type
         test removes it and a width test would only cost us real panels. */
      return section.renderedPanels.filter(function (p) {
        return p.type !== 'gate' && p.type !== 'hinge' && p.type !== 'hinge-panel' &&
               !p.isGate && !p.isRaked;
      }).map(function (p) { return p.width; });
    }
    var c = calculation || {};
    var raw = isArr(c.panelWidths) ? c.panelWidths
            : Array(c.regularPanels || c.numPanels || 0).fill(c.panelWidth);
    return gateLen ? raw.filter(function (w) { return w !== gateLen; }) : raw;
  }

  /* One pass, two counts, and a panel can only be in one of them. */
  function classify(widths) {
    var out = { small: 0, cut: 0 };
    for (var i = 0; i < widths.length; i++) {
      var w = Math.round(num(widths[i]));
      if (!(w > 0)) continue;
      if (w <= SMALL_MAX) out.small++;        /* a 1m kit, which already has the parts */
      else if (!FULL[w]) out.cut++;           /* cut off a full kit, so it needs them */
    }
    return out;
  }

  /* `from` is where the current section's lines start. See swapToOneMetreKit. */
  function findLine(list, test, from) {
    for (var i = from || 0; i < list.length; i++) {
      if (list[i] && test(list[i])) return i;
    }
    return -1;
  }

  /* Move `n` panels off the 2 metre kit line and onto the 1m kit.
     Returns the change in cost, or null if it could not be done cleanly - in which
     case nothing has been touched and the quote stands as the calculator built it.

     2026-09-11: the search is confined to the lines THIS section just wrote (bdFrom /
     ciFrom are the array lengths before calculatePanelCosts ran). It used to take the
     first 2 metre kit in the WHOLE job, so a small panel in a later section deleted an
     EARLIER section's kit - measured at 2 sections (1800, 3600): section 1 lost its 2m
     kit, section 2 carried two. The splice also shifted every later line back one place,
     so updateQuoteDisplay's per-section tagging loop (which starts at the index where the
     section began) missed this section's first line - its posts rendered untagged as
     "Whole job $159.54". It showed up first at 7+ sections only because that length set
     put the first small panel after a 2m section; it was never a section-count limit. */
  function swapToOneMetreKit(gc, n, colour, bdFrom, ciFrom) {
    var prod = resolve(SKU_1M[colour]);
    if (!prod) {
      if (!warned1m) {
        warned1m = 1;
        try { console.warn(LOG, 'panels of', SMALL_MAX + 'mm or less present but', SKU_1M[colour],
              'does not resolve to a variant - leaving them on the 2 metre kit. Activate the ' +
              '1m PerfMesh Panel Kit in Shopify and this starts working with no code change.'); } catch (e) {}
      }
      return null;
    }

    var bd = gc.state.quoteBreakdown || [];
    var li = findLine(bd, function (l) { return KIT_2M.test(String(l.sku || '')); }, bdFrom);
    if (li < 0) return null;                            /* no 2m line to take them off */
    var line = bd[li];
    var have = num(line.quantity);
    if (have < n) return null;                          /* not the grouping we expect */

    var unit2m = num(line.unitPrice);
    var delta = round2(prod.price * n - unit2m * n);

    /* the 2 metre line */
    var left = have - n;
    if (left > 0) {
      line.quantity = left;
      line.totalPrice = round2(unit2m * left);
    } else {
      bd.splice(li, 1);
    }

    /* mirror it in the cart before adding anything, so a failure part-way cannot
       leave the cart holding kits the breakdown no longer shows */
    var items = gc.state.cartItems || (gc.state.cartItems = []);
    var ci = findLine(items, function (c) {
      return c.variantId === line.variantId || String(c.sku || '') === String(line.sku || '');
    }, ciFrom);
    if (ci >= 0) {
      if (left > 0) items[ci].quantity = left;
      else items.splice(ci, 1);
    }

    var label = 'PerfMesh Pool Fencing Panel Kit — 1 Metre, ' + LABEL[colour];
    bd.push({
      category: 'Panel Kits',
      item: label,
      sku: prod.sku,
      quantity: n,
      unitPrice: prod.price,
      totalPrice: round2(prod.price * n),
      variantId: prod.variantId
    });
    items.push({ variantId: prod.variantId, quantity: n, price: prod.price,
                 title: label, sku: prod.sku });

    try { console.log(LOG, n, 'panel(s) of', SMALL_MAX + 'mm or less —', prod.sku, '×', n,
                      '(was 2m kit) — $' + delta); } catch (e) {}
    return delta;
  }

  function install(gc) {
    var proto = Object.getPrototypeOf(gc);
    if (!proto || typeof proto.calculatePanelCosts !== 'function') return false;
    if (proto.__cfCutPartsWrapped) return true;
    var original = proto.calculatePanelCosts;

    proto.calculatePanelCosts = function (section, calculation) {
      /* where this section's lines will start - the swap must not reach before it */
      var bdFrom = 0, ciFrom = 0;
      try {
        if (isArr(this.state.quoteBreakdown)) bdFrom = this.state.quoteBreakdown.length;
        if (isArr(this.state.cartItems)) ciFrom = this.state.cartItems.length;
      } catch (e) {}
      var added = original.apply(this, arguments);
      try {
        /* Only PerfMesh frames its panels this way. Blade and flat top bill
           brackets of their own, so leave them alone.

           There is no instance field to test: all three calculators write
           calcType only into the saved quote payload, and getActiveMeshSheet
           exists on all three. _syncRetainedHeightFromSheet is one of the five
           methods the perfmesh bundle has and the other two do not, so it is
           the honest marker. If it ever disappears this stands down rather
           than guessing, which is the right way round. */
        if (typeof this._syncRetainedHeightFromSheet !== 'function') return added;

        var counts = classify(widthsFor(this, section, calculation));
        var colour = colourKey(this, section);
        this.state.quoteBreakdown = this.state.quoteBreakdown || [];

        /* small panels first: the swap reads the 2 metre kit line the calculator just
           wrote, and the conversion-kit line pushed below must not be in the way */
        if (counts.small) {
          var moved = swapToOneMetreKit(this, counts.small, colour, bdFrom, ciFrom);
          if (moved !== null) added = num(added) + moved;
        }

        /* 2026-09-26, Pete: "the cut kits are just for when an off cut is used in a different
           section. we use the remaning sheet and top and bottome frame and add 2x 1105 side
           frame and clip and 2x channel." Cutting a kit down for its OWN panel needs no cut
           kit. This calculator never builds a panel from another section's offcut (the job
           cut plan does no offcut banking), so no cut kit is ever due. This supersedes the
           25 Aug rule ("per cut panel … 2x 1105 frame, clip, channel"). If offcut sharing is
           ever added, charge one kit per panel MADE FROM an offcut, here. */
        var qty = 0;   // was counts.cut
        if (!qty) return added;

        var prod = resolve(SKU[colour]);
        if (!prod) {
          if (!warned) {
            warned = 1;
            try { console.warn(LOG, 'cut panels present but', SKU[colour],
                  'does not resolve to a variant - leaving the part off the quote ' +
                  'rather than shipping a line that fails at checkout'); } catch (e) {}
          }
          return added;
        }

        var total = round2(prod.price * qty);
        this.state.quoteBreakdown = this.state.quoteBreakdown || [];
        this.state.quoteBreakdown.push({
          category: 'Panel Kits',
          /* No parts list in the line item. It read "(2x frame, 2x clip, 2x channel)"
             and went stale the moment Pete added the channel caps and screws on
             2026-08-26 - a customer was being shown a contents list that was missing
             two of the things they were paying for. The product page carries the
             contents and is the one place that has to be kept right. */
          item: 'PerfMesh Cut Panel Conversion Kit — ' + LABEL[colour],
          sku: prod.sku,
          quantity: qty,
          unitPrice: prod.price,
          totalPrice: total,
          variantId: prod.variantId
        });
        this.state.cartItems = this.state.cartItems || [];
        this.state.cartItems.push({
          variantId: prod.variantId, quantity: qty,
          price: prod.price, title: 'PerfMesh Cut Panel Conversion Kit — ' + LABEL[colour],
          sku: prod.sku
        });
        try { console.log(LOG, qty, 'cut panel(s) —', prod.sku, '×', qty, '= $' + total); } catch (e) {}
        return num(added) + total;
      } catch (err) {
        try { console.warn(LOG, err); } catch (e) {}
        return added;
      }
    };
    proto.__cfCutPartsWrapped = 1;
    try { console.log(LOG, 'installed'); } catch (e) {}
    return true;
  }

  var tries = 0;
  (function wait() {
    var gc = window.glassCalculator || window.glassFencingCalculator;
    if (gc && install(gc)) return;
    if (++tries > 200) return;
    setTimeout(wait, 100);
  })();
})();
